import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import Grid from '@material-ui/core/Grid';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import Toast from '@base/Toast';
import Loading from '@base/Loading';
import Title from '@base/Title';
import Breadcrumbs from '@base/Breadcrumbs';
import { ButtonSecondary, ButtonDefault } from '@base/Button/Button';

import EscolaService from '@services/EscolaService';

import WrapperCartaoTratamento from './WrapperCartaoTratamento';

import useStatusPendencia from '@hooks/enums/useStatusPendencia';
import useOrigemProblema from '@hooks/enums/useOrigemProblema';
import useStatusCartao from '@hooks/enums/useStatusCartao';

const TratamentoNomesDuplicados = () => {
  const { processo, codigo } = useParams();
  const history = useHistory();
  const location = useLocation();
  const toastRef = useRef();

  const id = location.state?.id;

  const breadcrumbs = [{
    path: `/${processo}/tratamentosDeNomesDuplicados`,
    name: 'Tratamento de Nomes Duplicados',
  }];

  const listagemStatus = useStatusPendencia();
  const listagemOrigemProblema = useOrigemProblema();
  const listagemStatusCartao = useStatusCartao();

  const [cartaoPrincipal, setCartaoPrincipal] = useState(null);
  const [cartoes, setCartoes] = useState([]);
  const [blob, setBlob] = useState({});
  const [loading, setLoading] = useState(true);
  const [proximoCartao, setProximoCartao] = useState(null);
  const [horaUltimoRefresh, setHoraUltimoRefresh] = useState();

  const baixarBlob = useCallback((cartaoId) => {
    EscolaService.downloadArquivoCartaoSegmentado(processo, undefined, cartaoId)
      .then(res => {
        setBlob(prev => ({ ...prev, [cartaoId]: res.data }));
      })
      .catch(err => console.error('Erro ao baixar blob:', err));
  }, [processo]);

  const carregar = useCallback(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setBlob({});
    setCartoes([]);

    EscolaService.buscaDadosDoCartao(processo, id)
      .then(res => {
        const cartaoPrincipal = res.data;
        setCartaoPrincipal(cartaoPrincipal);

        baixarBlob(cartaoPrincipal.id);

        const filtros = [
          { field: 'nome', value: cartaoPrincipal.nomeAluno },
          { field: 'inep', value: cartaoPrincipal.codigoInep },
        ];

        return EscolaService.buscarCartoes(processo, filtros)
          .then(r => {

            const outrosCartoes = r.data.content.filter(
              c => c.id !== cartaoPrincipal.id
            );

            const promises = outrosCartoes.map(c =>
              EscolaService.buscaDadosDoCartao(processo, c.id)
            );

            return Promise.all(promises);
          });

      }).then(resultados => {
        const cartoesDetalhados = resultados
          .map(res => res.data)
          .sort((a, b) => {
            const isPendenteA = a.status === 'COM_PENDENCIA_NOME_DUPLICADO';
            const isPendenteB = b.status === 'COM_PENDENCIA_NOME_DUPLICADO';

            if (isPendenteA && !isPendenteB) return -1;
            if (!isPendenteA && isPendenteB) return 1;
            return 0;
          });

        setCartoes(cartoesDetalhados);

        cartoesDetalhados.forEach(c => baixarBlob(c.id));
      })
      .catch(err => {
        toastRef.current?.show('error', 'Erro ao carregar dados do cartão.');
      })
      .finally(() => setLoading(false));
  }, [id, processo, baixarBlob]);

  const carregarProximo = useCallback(() => {
    if (!processo) return;

    EscolaService.buscarCartoes(processo, [
      { field: 'somenteComTratamentoNomeDuplicado', value: 'true' },
    ])
      .then(r => {
        const proximo = r.data.content.find(
          c => String(c.id) !== String(id) && c.nomeAluno !== cartaoPrincipal?.nomeAluno && c.tratamentoNomeDuplicado.status !== 'RESOLVIDA'
        );
        setProximoCartao(proximo || null);
      })
      .catch(err => console.error(err));
  }, [processo, id, cartaoPrincipal?.nomeAluno]);

  useEffect(() => {
    if (!id) {
      toastRef.current?.show('error', 'ID do cartão não encontrado.');
      history.push(`/${processo}/tratamentosDeNomesDuplicados`);
      return;
    }

    carregar();
    carregarProximo();
  }, [id, horaUltimoRefresh, carregar, carregarProximo, processo, history]);

  const todos = cartaoPrincipal ? [cartaoPrincipal, ...cartoes] : [];

  const onUpdateCartoes = () => setHoraUltimoRefresh(new Date());

  return (
    <>
      <Toast ref={toastRef} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Breadcrumbs links={breadcrumbs} active={`${codigo}`} />
        </Grid>
      </Grid>

      <Title>Tratar Nome Duplicado na Escola</Title>
      <br />

      {loading || !listagemStatusCartao.data ? (
        <Loading />
      ) : (
        <>
          <Grid container spacing={2}>

            {todos.length === 0 ? (
              <Grid item xs={12}>
                <Alert severity="warning">Nenhum cartão encontrado.</Alert>
              </Grid>
            ) : (
              todos.map(c => (
                <Grid item xs={12} md={6} key={c.id}>
                  <WrapperCartaoTratamento
                    cartao={c}
                    processo={processo}
                    blob={blob[c.id]}
                    todosCartoes={todos}
                    listagemStatusCartao={listagemStatusCartao.data}
                    listagemStatus={listagemStatus.data}
                    listagemOrigemProblema={listagemOrigemProblema.data}
                    onUpdateCartoes={onUpdateCartoes}
                    toastRef={toastRef}
                  />
                </Grid>
              ))
            )}

          </Grid>

          <Grid container spacing={2} alignItems="flex-end" style={{ marginTop: 16 }}>

            {proximoCartao && (
              <Grid item sm={12} lg={4}>
                <ButtonDefault
                  startIcon={<ArrowForwardIcon />}
                  style={{ margin: 4 }}
                  onClick={() =>
                    history.push({
                      pathname: `/${processo}/tratamentosDeNomesDuplicados/${proximoCartao.codigo}`,
                      state: { id: proximoCartao.id },
                    })
                  }
                  name="Ir Para o Próximo Tratamento"
                />
              </Grid>
            )}

            <Grid item sm={12} lg={12}>
              <ButtonSecondary
                startIcon={<ArrowBackIcon />}
                style={{ margin: 4 }}
                onClick={history.goBack}
                name="Voltar"
              />
            </Grid>

          </Grid>
        </>
      )}
    </>
  );
};

export default TratamentoNomesDuplicados;
